/* FIDNO motion system — GSAP + ScrollTrigger, orchestrated scroll-position
 * choreography rather than trigger-once entrances.
 *
 * Usage in sections/snippets:
 *   data-fidno-reveal            — scroll-scrubbed entrance (default: fade + rise)
 *   data-fidno-reveal="fade"     — opacity only
 *   data-fidno-reveal="scale"    — subtle scale + fade
 *   data-fidno-stagger           — on a parent: reveal children as a scrubbed cascade
 *   data-fidno-parallax          — scroll-scrubbed drift (yPercent -5..5 by default)
 *   data-fidno-parallax="1.6"    — same drift, scaled by this depth factor (layering)
 *   data-fidno-flash             — on a section: the ember line sweeps in as you
 *                                   scroll into the section, not on a fixed timer
 *   data-fidno-pulse             — on buttons: expanding ring pulse on click
 *   data-fidno-pulse="glow"      — soft radial glow flash instead (menu/icon triggers)
 *
 * All of the above except pulse/glow are scroll-scrubbed: their state tracks
 * scroll position directly (scrub, not a fixed-duration tween triggered once),
 * so scrolling back up un-reveals them. Above-the-fold content still reads as
 * "already there" on load — ScrollTrigger resolves initial progress against
 * the page's starting scroll position, it doesn't require a scroll gesture.
 *
 * Public API (for later milestones — menu cascade, section flashes, hero):
 *   FIDNO.motion.pulse(el), FIDNO.motion.glow(el), FIDNO.motion.initReveals(root),
 *   FIDNO.motion.initParallax(root), FIDNO.motion.initSectionFlash(root),
 *   FIDNO.motion.tokens — durations (s) and easing names shared with CSS variables.
 *
 * Motion is skipped entirely when the html element lacks the `fidno-motion` class
 * (theme setting off, or visitor prefers reduced motion — see theme.liquid).
 */

(() => {
  'use strict';

  const root = document.documentElement;
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  const active = () => root.classList.contains('fidno-motion') && !reducedMotion.matches && window.gsap;

  /* Read a duration token from CSS so JS and CSS never drift. Returns seconds. */
  const cssSeconds = (name, fallback) => {
    const raw = getComputedStyle(root).getPropertyValue(name).trim();
    if (raw.endsWith('ms')) return parseFloat(raw) / 1000;
    if (raw.endsWith('s')) return parseFloat(raw);
    return fallback;
  };

  const tokens = {
    instant: cssSeconds('--fidno-dur-instant', 0.12),
    fast: cssSeconds('--fidno-dur-fast', 0.2),
    base: cssSeconds('--fidno-dur-base', 0.35),
    slow: cssSeconds('--fidno-dur-slow', 0.65),
    easeOut: 'expo.out',
    easeEnergetic: 'back.out(1.7)',
    easeInOut: 'power4.inOut',
    /* scrub smoothing (seconds of catch-up lag) — a touch of ease without
       breaking the scroll-position link scrub:true would otherwise be. */
    scrub: 0.35,
  };

  /* ---- scroll-scrubbed reveals ----------------------------------------- */

  const revealed = new WeakSet();

  const revealVars = (el) => {
    switch (el.getAttribute('data-fidno-reveal')) {
      case 'fade':
        return { from: { opacity: 0 }, to: { opacity: 1 } };
      case 'scale':
        return { from: { opacity: 0, scale: 0.96 }, to: { opacity: 1, scale: 1 } };
      default:
        return { from: { opacity: 0, y: 28 }, to: { opacity: 1, y: 0 } };
    }
  };

  function initReveals(scope) {
    if (!active()) return;
    const container = scope || document;

    container.querySelectorAll('[data-fidno-reveal]').forEach((el) => {
      if (revealed.has(el)) return;
      revealed.add(el);
      const vars = revealVars(el);
      window.gsap.fromTo(el, vars.from, {
        ...vars.to,
        ease: 'none',
        scrollTrigger: { trigger: el, start: 'top 92%', end: 'top 55%', scrub: tokens.scrub },
      });
    });

    container.querySelectorAll('[data-fidno-stagger]').forEach((el) => {
      if (revealed.has(el) || !el.children.length) return;
      revealed.add(el);
      const tl = window.gsap.timeline({
        scrollTrigger: { trigger: el, start: 'top 90%', end: 'top 40%', scrub: tokens.scrub },
      });
      tl.fromTo(el.children, { opacity: 0, y: 24 }, { opacity: 1, y: 0, ease: 'none', stagger: 0.08 });
    });
  }

  /* ---- parallax + section flash ---------------------------------------- */

  /* [data-fidno-parallax] — scroll-scrubbed drift. The element should sit
     inside an overflow:hidden container with a little scale headroom (see
     .fidno-hero__media-inner). Optional value scales the drift range for
     layered depth: data-fidno-parallax="1.6" drifts further than the
     unscaled default (equivalent to "1"), data-fidno-parallax="0.5" drifts
     less — put a stronger value on a background layer and a lighter one on
     a foreground layer in the same section to read as depth, not one plane. */
  function initParallax(scope) {
    if (!active()) return;
    (scope || document).querySelectorAll('[data-fidno-parallax]').forEach((el) => {
      if (el.dataset.fidnoParallaxInit) return;
      el.dataset.fidnoParallaxInit = '1';
      const raw = parseFloat(el.getAttribute('data-fidno-parallax'));
      const depth = Number.isFinite(raw) && raw > 0 ? raw : 1;
      const range = 5 * depth;
      window.gsap.fromTo(
        el,
        { yPercent: -range },
        {
          yPercent: range,
          ease: 'none',
          scrollTrigger: { trigger: el.parentElement, start: 'top bottom', end: 'bottom top', scrub: true },
        }
      );
    });
  }

  /* [data-fidno-flash] — the signature section transition: a thin ember line
     sweeps across the section's top edge, tied to how far you've scrolled
     into it — not a fixed-duration flash. Sections opt in via data-fidno-flash. */
  function initSectionFlash(scope) {
    if (!active()) return;
    (scope || document).querySelectorAll('[data-fidno-flash]').forEach((sectionEl) => {
      if (sectionEl.dataset.fidnoFlashInit) return;
      sectionEl.dataset.fidnoFlashInit = '1';
      const line = document.createElement('span');
      line.className = 'fidno-flash-line';
      sectionEl.prepend(line);
      window.gsap
        .timeline({
          scrollTrigger: { trigger: sectionEl, start: 'top 85%', end: 'top 40%', scrub: tokens.scrub },
        })
        .fromTo(line, { scaleX: 0, opacity: 1 }, { scaleX: 1, ease: 'none' }, 0)
        .to(line, { opacity: 0, ease: 'none' }, 0.75);
    });
  }

  /* ---- mobile menu cascade -------------------------------------------- */

  /* Dawn's header-drawer toggles a `menu-opening` class on its <details>
     (see component-menu-drawer.css); CSS owns the panel slide, GSAP owns the
     staggered item cascade so nav items arrive one after another. This one
     stays a fixed-duration open/close interaction, not scroll-linked — there
     is no scroll position to sync a drawer to.
     One document-level observer, because section DOM gets replaced wholesale
     (theme editor, CLI hot reload) and per-element observers die with it. */
  let menuObserver = null;

  function initMenuDrawer() {
    if (!active() || menuObserver) return;
    menuObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        const drawer = mutation.target;
        if (!(drawer instanceof Element) || !drawer.matches('header-drawer details')) continue;
        const opening = drawer.classList.contains('menu-opening');
        const wasOpening = drawer.dataset.fidnoMenuOpen === '1';
        if (opening === wasOpening) continue;
        drawer.dataset.fidnoMenuOpen = opening ? '1' : '';
        const items = drawer.querySelectorAll('.menu-drawer__navigation > .menu-drawer__menu > li');
        if (!items.length) continue;
        if (opening) {
          window.gsap.fromTo(
            items,
            { opacity: 0, y: 24 },
            { opacity: 1, y: 0, duration: tokens.base, ease: tokens.easeOut, stagger: 0.06, delay: 0.12, overwrite: true }
          );
        } else {
          window.gsap.killTweensOf(items);
          window.gsap.set(items, { clearProps: 'opacity,transform' });
        }
      }
    });
    menuObserver.observe(document.documentElement, { attributes: true, subtree: true, attributeFilter: ['class'] });
  }

  /* ---- pulse reactions ------------------------------------------------ */

  /* Quick expanding ring — sharp and immediate (add to cart, submit). */
  function pulse(el) {
    if (!active() || !el) return;
    const rect = el.getBoundingClientRect();
    const ring = document.createElement('span');
    ring.className = 'fidno-pulse-ring';
    ring.style.left = `${rect.left}px`;
    ring.style.top = `${rect.top}px`;
    ring.style.width = `${rect.width}px`;
    ring.style.height = `${rect.height}px`;
    ring.style.borderRadius = getComputedStyle(el).borderRadius;
    document.body.appendChild(ring);

    window.gsap.fromTo(
      ring,
      { opacity: 0.9, scale: 1 },
      {
        opacity: 0,
        scale: 1.35,
        duration: tokens.base,
        ease: 'power2.out',
        onComplete: () => ring.remove(),
      }
    );
  }

  /* Soft glow flash — sets mood, not sharp (menu open/close). */
  function glow(el) {
    if (!active() || !el) return;
    const rect = el.getBoundingClientRect();
    const size = Math.max(rect.width, rect.height) * 2.5;
    const halo = document.createElement('span');
    halo.className = 'fidno-pulse-glow';
    halo.style.width = `${size}px`;
    halo.style.height = `${size}px`;
    halo.style.left = `${rect.left + rect.width / 2 - size / 2}px`;
    halo.style.top = `${rect.top + rect.height / 2 - size / 2}px`;
    document.body.appendChild(halo);

    window.gsap.fromTo(
      halo,
      { opacity: 0, scale: 0.6 },
      {
        opacity: 1,
        scale: 1,
        duration: tokens.fast,
        ease: 'power1.out',
        yoyo: true,
        repeat: 1,
        onComplete: () => halo.remove(),
      }
    );
  }

  /* ---- global scroll state (header/announcement bar compact treatment) --
     Runs unconditionally, independent of the motion-enabled gate: whether
     the header/announcement bar compact once you've scrolled is a layout
     adaptation, not decoration, so it must work even with motion off or
     reduced-motion — only the transition smoothness (CSS) respects those. */

  function initScrollState() {
    const threshold = 24;
    let ticking = false;
    const apply = () => {
      root.classList.toggle('fidno-scrolled', window.scrollY > threshold);
      ticking = false;
    };
    window.addEventListener(
      'scroll',
      () => {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(apply);
      },
      { passive: true }
    );
    apply();
  }

  /* ---- wiring ----------------------------------------------------------- */

  function start() {
    if (!active()) return;
    window.gsap.registerPlugin(window.ScrollTrigger);
    initReveals(document);
    initParallax(document);
    initSectionFlash(document);
    initMenuDrawer();

    document.addEventListener('click', (event) => {
      const trigger = event.target.closest('[data-fidno-pulse]');
      if (!trigger) return;
      if (trigger.getAttribute('data-fidno-pulse') === 'glow') glow(trigger);
      else pulse(trigger);
    });

    /* Theme editor: sections re-render in place. */
    document.addEventListener('shopify:section:load', (event) => {
      initReveals(event.target);
      initParallax(event.target);
      initSectionFlash(event.target);
    });
  }

  /* If the visitor switches to reduced motion mid-session, surface everything. */
  reducedMotion.addEventListener('change', () => {
    if (!reducedMotion.matches) return;
    root.classList.remove('fidno-motion');
    if (window.gsap) window.gsap.set('[data-fidno-reveal], [data-fidno-stagger] > *', { clearProps: 'all', opacity: 1 });
  });

  window.FIDNO = window.FIDNO || {};
  window.FIDNO.motion = { pulse, glow, initReveals, initParallax, initSectionFlash, initMenuDrawer, tokens };

  initScrollState();
  start();
})();
